<script setup lang="ts">
import BuildingLinkCard from '@/components/cards/BuildingLinkCard.vue';
import type { Building } from '@/typings/buildings/Building.ts';
import { useRentalLocalizedUrls } from '@/composables/useRentalLocalizedUrls.ts';
import { useI18n } from 'vue-i18n';
import type { LocaleSchema } from '@/locales/index.ts';

const { t, locale } = useI18n<{message: LocaleSchema}>({
  useScope: 'global',
});

const selectedBuilding = defineModel<Building>('selected');
const hoveredBuilding = defineModel<Building>('hovered');
const { homeUrl, getBuildingWizardUrl } = useRentalLocalizedUrls();

const props = defineProps<{
  buildings: Building[];
  loading?: boolean;
  isSelectable?: boolean;
  mode?: 'rental' | 'purchase';
}>();

const emit = defineEmits<{
  'building-selected': [building: Building];
}>();

function hasAvailableUnits(building: Building): boolean {
  return building.availableUnits !== null &&
         building.availableUnits !== undefined &&
         building.availableUnits > 0;
}

function onBuildingClick(building: Building) {
  if (props.isSelectable && hasAvailableUnits(building)) {
    selectedBuilding.value = building;
    emit('building-selected', building);
  }
}

function onBuildingHover(building: Building | undefined) {
  if (props.isSelectable) {
    hoveredBuilding.value = building;
  }
}
</script>

<template>
  <div class="buildingList">
    <div v-if="loading" class="buildingList-loadingState">
      <div class="buildingList-skeleton preferredColumnCountGrid">
        <div v-for="i in 3" :key="i" class="buildingList-skeletonCard">
          <div class="skeleton-content">
            <div class="skeleton-image"></div>
            <div class="skeleton-text-lines">
              <div class="skeleton-line skeleton-line--title"></div>
              <div class="skeleton-line skeleton-line--subtitle"></div>
              <div class="skeleton-line skeleton-line--description"></div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div v-else-if="buildings.length > 0" 
         class="buildingList-buildingGrid preferredColumnCountGrid">
      <BuildingLinkCard v-for="building in buildings"
                        :key="building.id"
                        class="buildingLinkList-buildingCard"
                        :class="{
                          'buildingLinkList-buildingCard--selected': isSelectable && selectedBuilding?.id === building.id,
                          'buildingLinkList-buildingCard--hovered': isSelectable && hoveredBuilding?.id === building.id,
                          'buildingLinkList-buildingCard--selectable': isSelectable,
                          'buildingLinkList-buildingCard--unavailable': !hasAvailableUnits(building)
                        }"
                        :mode="mode"
                        :building="building"
                        :href="!isSelectable ? getBuildingWizardUrl(building) : ''"
                        @click="isSelectable ? onBuildingClick(building) : undefined"
                        @mouseenter="isSelectable && hasAvailableUnits(building) ? onBuildingHover(building) : undefined"
                        @mouseleave="isSelectable && hasAvailableUnits(building) ? onBuildingHover(undefined) : undefined">
        <template #extra-info>
          <slot name="extra-info"
                :building="building">
          </slot>
        </template>
        <template #footer>
          <slot name="footer"
                :building="building">
          </slot>
        </template>
      </BuildingLinkCard>
    </div>
    <div v-else
         class="buildingList-emptyState">
      <h3>{{ t('browsingApp.buildings.noBuildingsAvailable') }}</h3>
    </div>
  </div>
</template>

<style lang="scss">
.buildingList {
  &-buildingGrid, &-skeleton {
    --column-count: 2;
    --column-gap: var(--spc24-16);
    max-width: 100%;
  }

  &-skeletonCard {
    background: var(--gray-50);
    border-radius: 8px;
    border: 1px solid var(--gray-200);
    overflow: hidden;
    
    .skeleton-content {
      padding: 0;
      
      .skeleton-image {
        height: 200px;
        background: linear-gradient(90deg, var(--gray-200) 25%, var(--gray-100) 50%, var(--gray-200) 75%);
        background-size: 200% 100%;
        animation: skeleton-loading 1.5s infinite;
      }
      
      .skeleton-text-lines {
        padding: 1rem;
        
        .skeleton-line {
          height: 12px;
          background: linear-gradient(90deg, var(--gray-200) 25%, var(--gray-100) 50%, var(--gray-200) 75%);
          background-size: 200% 100%;
          animation: skeleton-loading 1.5s infinite;
          border-radius: 4px;
          margin-bottom: 8px;
          
          &--title {
            height: 16px;
            width: 75%;
          }
          
          &--subtitle {
            width: 50%;
          }
          
          &--description {
            width: 90%;
            margin-bottom: 0;
          }
        }
      }
    }
  }

  &-emptyState {
    text-align: center;
    padding: 2rem;
    color: var(--gray-500);

    h3 {
      margin: 0;
      font-weight: 500;
      font-style: italic;
    }
  }
}

@keyframes skeleton-loading {
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
}

/* Selectable building card styles */
.buildingLinkList-buildingCard {
  &--selectable {
    cursor: pointer;
    
    a {
      pointer-events: none; // Désactiver le lien normal quand sélectionnable
    }
    
    .cardTopImage_buildingLinkCard {
      transition: all 0.2s ease;
    }
  }
  
  &--hovered:not(&--selected) .cardTopImage_buildingLinkCard {
    outline-color: var(--primary-300);
    outline-offset: -2px;
    transform: translateY(-2px);
  }
  
  &--selected .cardTopImage_buildingLinkCard {
    outline: 3px solid var(--primary-500);
    outline-offset: -3px;
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  }
  
  &--unavailable {
    cursor: default;
    
    &:hover .cardTopImage_buildingLinkCard {
      outline-color: transparent !important;
      transform: none !important;
    }
  }
}
</style>